/**
 * @name product
 *
 * @description
 * Insert any utilities about products here.
 */
import {ProductType, FullProductType} from '_types'

export const isShopByLook = (product: ProductType) => product.type?.set === true

/**
 * Whether a single look/set member is online.
 *
 * Set members do NOT carry a root-level `online` flag (unlike the backend contract originally
 * assumed). Instead, an online member resolves to at least one online variation group in
 * `c_variationGroupsData`; an offline/unavailable member comes back with an empty
 * `c_variationGroupsData` (and `c_colorsQuantity: 0`). We therefore treat a member as online iff
 * it has at least one variation group with `online === true`. This mirrors the classic PDP
 * visibility logic, which keys off the variation group's `online` flag.
 */
const isSetMemberOnline = (member: FullProductType | null | undefined): boolean =>
    member != null && (member.c_variationGroupsData?.some((vg) => vg.online === true) ?? false)

/**
 * Returns the online, resolved members of a "shop by look" product set.
 *
 * Entries that are missing/unresolved (null/undefined) or offline (no online variation group) are
 * filtered out so they do not render as empty tiles. Returns `[]` when the set data is not present.
 */
export const getOnlineSetProducts = (product: FullProductType): FullProductType[] =>
    product.c_setData?.productSetProducts?.filter(isSetMemberOnline) ?? []

/**
 * Whether a "shop by look" product set has at least one online, resolved member. Used by the PDP
 * visibility block to 404/redirect an empty look, mirroring the classic offline-model handling.
 */
export const hasOnlineSetProducts = (product: FullProductType): boolean => getOnlineSetProducts(product).length > 0
